// Presentation helpers for a cell's validation error, kept pure so both the
// grid cell (`cell.tsx`) and the Expanded Cell View panel share one contract.

/**
 * Visual divider between two DIFFERENT error messages shown on one cell.
 */
export const CELL_ERROR_MESSAGE_SEPARATOR = '\n— — —\n';

/**
 * Combines the messages a cell collects from the error sources that are live in
 * the current agent-compiler mode (bot-compiler + agent-validator), dropping
 * empties and REPEATS.
 *
 * PSTUDIO-3990: the derived node-number errors ("Node number is missing…",
 * "Duplicate node number…") are written to BOTH indexes, so a plain join
 * printed the same sentence twice — in the cell tooltip and in the Expanded
 * Cell View — with a divider between the two copies. Deduplicating by text
 * (compared trimmed, first occurrence wins so the order is preserved) keeps the
 * divider meaningful: it only ever separates genuinely different messages.
 */
export function joinCellErrorMessages(
  messages: readonly (string | undefined | null)[]
): string | undefined {
  const unique: string[] = [];
  const seen = new Set<string>();
  for (const message of messages) {
    if (!message) continue;
    const key = message.trim();
    // A blank/whitespace-only source is not a message: joining it would add an
    // empty block and leave a dangling divider.
    if (!key || seen.has(key)) continue;
    seen.add(key);
    unique.push(message);
  }
  return unique.join(CELL_ERROR_MESSAGE_SEPARATOR) || undefined;
}

/**
 * Which of the two surfaces that describe the SAME cell is asking for an id.
 *
 * One cell is described in two places at once: the grid renders a hidden node
 * in the cell's tooltip portal, and the Expanded Cell View renders the message
 * visibly above its editor. Both are on screen together whenever the panel is
 * open on an errored cell, so they cannot share one `id` — a document with two
 * elements carrying the same id is invalid (WCAG 4.1.1), and `getElementById`
 * silently resolves BOTH `aria-describedby` references to whichever node
 * happens to come first in document order.
 */
export type CellErrorDescriptionSurface = 'cell' | 'panel';

/**
 * Id of the node that carries the cell's error text for the given surface,
 * referenced from that surface's `aria-describedby` (PSTUDIO-3990 a11y).
 * Derived from the cell id, which is already unique per row × column.
 *
 * `'cell'` (the default) is the visually-hidden node rendered in the grid
 * cell's tooltip portal; `'panel'` is the visible message in the Expanded Cell
 * View. The suffix is what keeps the two apart while the panel is open — see
 * `CellErrorDescriptionSurface`.
 */
export function cellErrorDescriptionId(
  cellId: string,
  surface: CellErrorDescriptionSurface = 'cell'
): string {
  return surface === 'panel' ? `${cellId}-error-panel` : `${cellId}-error`;
}

/**
 * The ARIA props an errored cell must carry so its state is programmatically
 * determinable and not conveyed by the red `c-editor-table-td--error` paint
 * alone (WCAG 1.3.1 / 1.4.1 / 3.3.1 — PSTUDIO-3990 review).
 *
 * `aria-invalid` is a GLOBAL ARIA state, so it is exposed on the cell's
 * `role="gridcell"`; `aria-describedby` points at the description node the
 * given surface renders (see `cellErrorDescriptionId`). Kept pure so it is
 * unit-testable — the vitest environment here is `node`, with no DOM to render
 * the cell into.
 */
export function resolveCellErrorAria(
  errorMessage: string | undefined,
  cellId: string,
  surface: CellErrorDescriptionSurface = 'cell'
): { 'aria-invalid'?: true; 'aria-describedby'?: string } {
  if (!errorMessage) return {};
  return {
    'aria-invalid': true,
    'aria-describedby': cellErrorDescriptionId(cellId, surface),
  };
}
